<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">

      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm p-6">
        <div class="flex justify-between items-start">
          <div>
            <p v-if="lastUpdated" class="text-xs text-gray-400">
              Last updated: {{ formatDate(lastUpdated) }}
            </p>
          </div>
          <button
            @click="loadAll"
            :disabled="loading"
            class="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm"
          >
            <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': loading }" />
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading && !settings" class="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
        <RefreshCw class="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
        Loading settings…
      </div>

      <template v-else>

        <!-- ── Enable / Disable Toggle ───────────────────────────────── -->
        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
          <div class="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div :class="isEnabled ? 'bg-green-100' : 'bg-gray-100'" class="p-2 rounded-lg">
                <ToggleRight v-if="isEnabled" class="w-5 h-5 text-green-600" />
                <ToggleLeft v-else class="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Constraint-Based Auto-Approval</h2>
                <p class="text-sm text-gray-500">
                  When enabled, qualifying clock-ins and clock-outs are approved automatically.
                </p>
              </div>
            </div>
            <!-- Toggle switch -->
            <button
              @click="handleToggle"
              :disabled="toggling"
              :class="[
                isEnabled ? 'bg-green-500' : 'bg-gray-300',
                'relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50'
              ]"
              role="switch"
              :aria-checked="isEnabled"
            >
              <span
                :class="[isEnabled ? 'translate-x-7' : 'translate-x-0', 'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform transition duration-200 ease-in-out']"
              />
            </button>
          </div>

          <div class="px-6 py-4">
            <div v-if="isEnabled" class="flex items-start gap-2 text-sm text-green-700 bg-green-50 rounded-lg p-3">
              <Info class="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Auto-approval is <strong>active</strong>. Attendance requests that pass all constraints below will be approved without admin action.</span>
            </div>
            <div v-else class="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
              <Info class="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Auto-approval is <strong>disabled</strong>. All attendance requests require manual admin approval.</span>
            </div>
          </div>
        </div>

        <!-- ── Reckless Auto-Approve Toggle ─────────────────────────── -->
        <div class="bg-white rounded-xl shadow-sm overflow-hidden" :class="{ 'opacity-50 pointer-events-none': !isEnabled }">
          <div class="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div :class="recklessEnabled ? 'bg-orange-100' : 'bg-gray-100'" class="p-2 rounded-lg">
                <Zap :class="recklessEnabled ? 'text-orange-500' : 'text-gray-400'" class="w-5 h-5" />
              </div>
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Reckless Auto-Approve</h2>
                <p class="text-sm text-gray-500">
                  Skips zone, time window, probation, violations and behavioral checks.
                  Only verifies: employee active · photo present · GPS accuracy · not a weekend/holiday.
                </p>
              </div>
            </div>
            <button
              @click="handleRecklessToggle"
              :disabled="!isEnabled || togglingReckless"
              :class="[
                recklessEnabled ? 'bg-orange-500' : 'bg-gray-300',
                'relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50'
              ]"
              role="switch"
              :aria-checked="recklessEnabled"
            >
              <span :class="[recklessEnabled ? 'translate-x-7' : 'translate-x-0', 'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform transition duration-200 ease-in-out']" />
            </button>
          </div>
          <div class="px-6 py-4">
            <div v-if="!isEnabled" class="flex items-start gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <Info class="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Enable Auto-Approval above before configuring Reckless mode.</span>
            </div>
            <div v-else-if="recklessEnabled" class="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 rounded-lg p-3">
              <Zap class="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span><strong>Reckless mode is active.</strong> Clock-ins and clock-outs on working days will be auto-approved as long as the employee is active, provides a photo, and has accurate GPS. Zone and time window checks are bypassed.</span>
            </div>
            <div v-else class="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
              <Info class="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Reckless mode is off. Standard constraint-based approval is active.</span>
            </div>
          </div>
        </div>

        <!-- ── Constraint Configuration ──────────────────────────────── -->
        <div class="bg-white rounded-xl shadow-sm overflow-hidden" :class="{ 'opacity-50': recklessEnabled }">
          <div class="px-6 py-5 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Sliders class="w-5 h-5 text-blue-600" />
              Constraint Configuration
              <span v-if="recklessEnabled" class="ml-2 text-xs font-normal text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">bypassed in reckless mode</span>
            </h2>
            <p class="text-sm text-gray-500 mt-1">
              Changes take effect immediately on save — no restart required.
            </p>
          </div>

          <div class="px-6 py-6 space-y-8">

            <!-- Time Windows -->
            <div>
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <Clock class="w-4 h-4 text-blue-500" />
                Allowed Time Windows
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div class="space-y-3">
                  <label class="block text-sm font-medium text-gray-700">Clock-In Window</label>
                  <div class="flex items-center gap-2">
                    <div>
                      <label class="block text-xs text-gray-500 mb-1">From</label>
                      <input type="time" v-model="form.clockInStartTime"
                        class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <span class="mt-4 text-gray-400">–</span>
                    <div>
                      <label class="block text-xs text-gray-500 mb-1">Until</label>
                      <input type="time" v-model="form.clockInEndTime"
                        class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>

                <div class="space-y-3">
                  <label class="block text-sm font-medium text-gray-700">Clock-Out Window</label>
                  <div class="flex items-center gap-2">
                    <div>
                      <label class="block text-xs text-gray-500 mb-1">From</label>
                      <input type="time" v-model="form.clockOutStartTime"
                        class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <span class="mt-4 text-gray-400">–</span>
                    <div>
                      <label class="block text-xs text-gray-500 mb-1">Until</label>
                      <input type="time" v-model="form.clockOutEndTime"
                        class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
              </div>
              <p class="text-xs text-gray-400 mt-2">
                Working days are fixed: Sunday – Thursday. Weekend/holiday work always requires manual approval.
              </p>
            </div>

            <!-- Location / GPS -->
            <div>
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <MapPin class="w-4 h-4 text-blue-500" />
                Location & GPS
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Max Distance from Office (metres)
                  </label>
                  <input type="number" v-model.number="form.maxDistanceMeters" min="1" max="5000"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <p class="text-xs text-gray-400 mt-1">
                    Employee must be within this distance of a saved field location.
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Max GPS Accuracy (metres)
                  </label>
                  <input type="number" v-model.number="form.maxGpsAccuracyMeters" min="1" max="200"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <p class="text-xs text-gray-400 mt-1">
                    Reported GPS accuracy must be better than this value (lower = stricter).
                  </p>
                </div>
              </div>
            </div>

            <!-- Photo & Probation -->
            <div>
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <ShieldCheck class="w-4 h-4 text-blue-500" />
                Employee Requirements
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p class="text-sm font-medium text-gray-700">Require Photo</p>
                    <p class="text-xs text-gray-500 mt-0.5">Clock-in/out must include a selfie photo</p>
                  </div>
                  <button
                    @click="form.requirePhoto = !form.requirePhoto"
                    :class="[form.requirePhoto ? 'bg-blue-500' : 'bg-gray-300', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none']"
                    role="switch"
                    :aria-checked="form.requirePhoto"
                  >
                    <span :class="[form.requirePhoto ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ease-in-out']" />
                  </button>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Probationary Period (days)
                  </label>
                  <input type="number" v-model.number="form.probationaryPeriodDays" min="0" max="365"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <p class="text-xs text-gray-400 mt-1">
                    Employees employed for fewer days than this are not eligible for auto-approval.
                  </p>
                </div>
              </div>
            </div>

            <!-- Validation errors -->
            <div v-if="formError" class="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0" />
              {{ formError }}
            </div>

            <!-- Save / Reset row -->
            <div class="flex items-center gap-3 pt-2 border-t border-gray-100">
              <button
                @click="saveSettings"
                :disabled="saving"
                class="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                <Save class="w-4 h-4" />
                {{ saving ? 'Saving…' : 'Save Constraints' }}
              </button>
              <button
                @click="confirmReset"
                :disabled="saving"
                class="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <RotateCcw class="w-4 h-4" />
                Reset to Defaults
              </button>
              <span v-if="saveSuccess" class="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle class="w-4 h-4" />
                Saved!
              </span>
            </div>
          </div>
        </div>

        <!-- ── Statistics ─────────────────────────────────────────────── -->
        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
          <div class="px-6 py-5 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart2 class="w-5 h-5 text-blue-600" />
              Approval Statistics
            </h2>
            <p class="text-sm text-gray-500 mt-1">Lifetime totals across all attendance records with an approval status.</p>
          </div>
          <div class="px-6 py-5">
            <div v-if="stats" class="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div class="bg-blue-50 rounded-lg p-4 text-center">
                <p class="text-2xl font-bold text-blue-700">{{ stats.total }}</p>
                <p class="text-xs text-blue-600 mt-1">Total Records</p>
              </div>
              <div class="bg-green-50 rounded-lg p-4 text-center">
                <p class="text-2xl font-bold text-green-700">{{ stats.approved }}</p>
                <p class="text-xs text-green-600 mt-1">Auto-Approved</p>
              </div>
              <div class="bg-yellow-50 rounded-lg p-4 text-center">
                <p class="text-2xl font-bold text-yellow-700">{{ stats.pending }}</p>
                <p class="text-xs text-yellow-600 mt-1">Pending Review</p>
              </div>
              <div class="bg-purple-50 rounded-lg p-4 text-center">
                <p class="text-2xl font-bold text-purple-700">{{ stats.autoApprovalRate }}</p>
                <p class="text-xs text-purple-600 mt-1">Approval Rate</p>
              </div>
            </div>
            <div v-else class="text-sm text-gray-400 italic">Statistics not available.</div>
          </div>
        </div>

        <!-- ── Policy Preview ─────────────────────────────────────────── -->
        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
          <div class="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Eye class="w-5 h-5 text-blue-600" />
              Policy Preview
            </h2>
            <button
              @click="loadPreview"
              :disabled="loadingPreview"
              class="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw class="w-3.5 h-3.5" :class="{ 'animate-spin': loadingPreview }" />
              Refresh
            </button>
          </div>
          <div class="px-6 py-5">
            <div v-if="preview" class="space-y-3">
              <div
                v-for="scenario in preview.scenarios"
                :key="scenario.scenario"
                class="flex items-start gap-3 p-3 rounded-lg"
                :class="scenario.expectedOutcome.startsWith('AUTO_APPROVED') ? 'bg-green-50' : 'bg-gray-50'"
              >
                <CheckCircle v-if="scenario.expectedOutcome.startsWith('AUTO_APPROVED')" class="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <XCircle v-else class="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p class="text-sm font-medium text-gray-800">{{ scenario.scenario }}</p>
                  <p class="text-xs text-gray-500 mt-0.5">{{ scenario.constraintNote }}</p>
                  <p class="text-xs font-medium mt-1"
                    :class="scenario.expectedOutcome.startsWith('AUTO_APPROVED') ? 'text-green-600' : 'text-gray-500'">
                    → {{ scenario.expectedOutcome }}
                  </p>
                </div>
              </div>
            </div>
            <div v-else class="text-sm text-gray-400 italic">
              Click Refresh to load the current policy preview.
            </div>
          </div>
        </div>

      </template>

    </div>

    <!-- Reset confirmation modal -->
    <div v-if="showResetConfirm" class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="bg-red-100 rounded-full p-2">
            <AlertCircle class="w-5 h-5 text-red-600" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Reset to Defaults?</h3>
        </div>
        <p class="text-sm text-gray-600 mb-6">
          All constraint settings will be restored to their default values. The auto-approval feature will also be <strong>disabled</strong>. This cannot be undone.
        </p>
        <div class="flex gap-3 justify-end">
          <button @click="showResetConfirm = false" class="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button @click="doReset" :disabled="saving" class="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
            {{ saving ? 'Resetting…' : 'Reset' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import {
  CheckCircle, XCircle, RefreshCw, ToggleLeft, ToggleRight,
  Sliders, Clock, MapPin, ShieldCheck, Save, RotateCcw,
  BarChart2, Eye, Info, AlertCircle, Zap
} from 'lucide-vue-next';
import autoApprovalSettingsService from '@/services/autoApprovalSettingsService';

// ── State ─────────────────────────────────────────────────────────────────

const loading       = ref(false);
const toggling      = ref(false);
const saving        = ref(false);
const loadingPreview = ref(false);
const saveSuccess   = ref(false);
const formError     = ref('');
const showResetConfirm = ref(false);

const settings      = ref(null);   // raw API response .data
const stats         = ref(null);
const preview       = ref(null);

// Local form state (mirrors constraint fields)
const form = ref({
  clockInStartTime:    '07:30',
  clockInEndTime:      '08:30',
  clockOutStartTime:   '16:30',
  clockOutEndTime:     '17:30',
  maxDistanceMeters:   50,
  maxGpsAccuracyMeters: 20,
  requirePhoto:        true,
  probationaryPeriodDays: 90,
});

const isEnabled        = ref(false);
const recklessEnabled  = ref(false);
const togglingReckless = ref(false);
const lastUpdated      = ref(null);

// ── Lifecycle ─────────────────────────────────────────────────────────────

onMounted(loadAll);

// ── Data loading ──────────────────────────────────────────────────────────

async function loadAll() {
  loading.value = true;
  try {
    await Promise.all([loadSettings(), loadStats()]);
  } finally {
    loading.value = false;
  }
}

async function loadSettings() {
  try {
    const res = await autoApprovalSettingsService.getSettings();
    const data = res.data;
    settings.value        = data;
    isEnabled.value       = data.enabled ?? data.isEnabled ?? false;
    recklessEnabled.value = data.recklessAutoApprove ?? false;
    lastUpdated.value     = data.lastUpdated;

    // Populate form from nested constraint structure
    const t = data.constraints?.time;
    const l = data.constraints?.location;
    const e = data.constraints?.employee;
    const tech = data.constraints?.technical;

    if (t?.clockInWindow)  { form.value.clockInStartTime  = t.clockInWindow.start;  form.value.clockInEndTime  = t.clockInWindow.end; }
    if (t?.clockOutWindow) { form.value.clockOutStartTime = t.clockOutWindow.start; form.value.clockOutEndTime = t.clockOutWindow.end; }
    if (l?.maxDistanceMeters    != null) form.value.maxDistanceMeters    = l.maxDistanceMeters;
    if (tech?.maxGpsAccuracyMeters != null) form.value.maxGpsAccuracyMeters = tech.maxGpsAccuracyMeters;
    if (tech?.requirePhoto      != null) form.value.requirePhoto         = tech.requirePhoto;
    if (e?.probationaryPeriodDays != null) form.value.probationaryPeriodDays = e.probationaryPeriodDays;
  } catch (err) {
    console.error('Failed to load auto-approval settings:', err);
  }
}

async function loadStats() {
  try {
    const res = await autoApprovalSettingsService.getStatistics();
    stats.value = res.data?.statistics || null;
  } catch (err) {
    console.warn('Failed to load statistics:', err);
  }
}

async function loadPreview() {
  loadingPreview.value = true;
  try {
    const res = await autoApprovalSettingsService.getPreview();
    preview.value = res.data;
  } catch (err) {
    console.warn('Failed to load preview:', err);
  } finally {
    loadingPreview.value = false;
  }
}

// ── Actions ───────────────────────────────────────────────────────────────

async function handleToggle() {
  toggling.value = true;
  try {
    await autoApprovalSettingsService.toggle(!isEnabled.value);
    // Reload from server to guarantee UI is in sync with DB
    await loadSettings();
  } catch (err) {
    console.error('Toggle failed:', err);
  } finally {
    toggling.value = false;
  }
}

async function handleRecklessToggle() {
  togglingReckless.value = true;
  try {
    await autoApprovalSettingsService.updateSettings({
      enabled:              isEnabled.value,
      recklessAutoApprove:  !recklessEnabled.value,
      clockInStartTime:     form.value.clockInStartTime,
      clockInEndTime:       form.value.clockInEndTime,
      clockOutStartTime:    form.value.clockOutStartTime,
      clockOutEndTime:      form.value.clockOutEndTime,
      maxDistanceMeters:    form.value.maxDistanceMeters,
      maxGpsAccuracyMeters: form.value.maxGpsAccuracyMeters,
      requirePhoto:         form.value.requirePhoto,
      probationaryPeriodDays: form.value.probationaryPeriodDays,
    });
    // Reload from server to guarantee UI is in sync with DB
    await loadSettings();
  } catch (err) {
    console.error('Reckless toggle failed:', err);
  } finally {
    togglingReckless.value = false;
  }
}

function validateForm() {
  if (!form.value.clockInStartTime || !form.value.clockInEndTime) {
    return 'Clock-in time window is required.';
  }
  if (form.value.clockInStartTime >= form.value.clockInEndTime) {
    return 'Clock-in start must be before clock-in end.';
  }
  if (!form.value.clockOutStartTime || !form.value.clockOutEndTime) {
    return 'Clock-out time window is required.';
  }
  if (form.value.clockOutStartTime >= form.value.clockOutEndTime) {
    return 'Clock-out start must be before clock-out end.';
  }
  if (form.value.maxDistanceMeters < 1) {
    return 'Max distance must be at least 1 metre.';
  }
  if (form.value.maxGpsAccuracyMeters < 1) {
    return 'Max GPS accuracy must be at least 1 metre.';
  }
  return null;
}

async function saveSettings() {
  formError.value = '';
  const err = validateForm();
  if (err) { formError.value = err; return; }

  saving.value = true;
  try {
    await autoApprovalSettingsService.updateSettings({
      enabled:               isEnabled.value,
      recklessAutoApprove:   recklessEnabled.value,
      clockInStartTime:      form.value.clockInStartTime,
      clockInEndTime:        form.value.clockInEndTime,
      clockOutStartTime:     form.value.clockOutStartTime,
      clockOutEndTime:       form.value.clockOutEndTime,
      maxDistanceMeters:     form.value.maxDistanceMeters,
      maxGpsAccuracyMeters:  form.value.maxGpsAccuracyMeters,
      requirePhoto:          form.value.requirePhoto,
      probationaryPeriodDays: form.value.probationaryPeriodDays,
    });
    saveSuccess.value = true;
    setTimeout(() => { saveSuccess.value = false; }, 3000);
    await loadSettings();
  } catch (err) {
    formError.value = err?.response?.data?.message || 'Failed to save settings.';
  } finally {
    saving.value = false;
  }
}

function confirmReset() {
  showResetConfirm.value = true;
}

async function doReset() {
  saving.value = true;
  try {
    await autoApprovalSettingsService.resetToDefaults();
    showResetConfirm.value = false;
    await loadSettings();
    saveSuccess.value = true;
    setTimeout(() => { saveSuccess.value = false; }, 3000);
  } catch (err) {
    formError.value = err?.response?.data?.message || 'Failed to reset settings.';
  } finally {
    saving.value = false;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDate(d) {
  if (!d) return 'Unknown';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}
</script>
